"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MockRetailSalesOrdersModule = void 0;
const inversify_1 = require("inversify");
const SalesOrder_1 = require("../../models/SalesOrder");
const mockSalesOrders_1 = require("./data/mockSalesOrders");
const currentUnixTimestamp_1 = require("../../../../../apis/spg/util/currentUnixTimestamp");
const guid_1 = require("../../../../../apis/spg/util/guid");
const EventEmitter_1 = require("../../../../../apis/next/util/EventEmitter");
const salesOrder_1 = require("../../types/salesOrder");
const IPromotion_1 = require("../../types/IPromotion");
let MockRetailSalesOrdersModule = class MockRetailSalesOrdersModule {
    /**
     * Setup injected dependencies.
     */
    constructor() {
        this.salesOrders = mockSalesOrders_1.mockSalesOrders;
        this.temporarySalesOrders = [];
        this._emitter = new EventEmitter_1.EventEmitter();
    }
    validateSalesOrder(salesOrder) {
        //I want to implement this but these mocks aren't being used for testing
        throw new Error("Method not implemented.");
    }
    /**
     * Update the relevant sales order timestamp field based on the current
     * status.
     */
    _setSalesOrderStatusTimestamp(salesOrder, status) {
        const now = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        switch (status) {
            case SalesOrder_1.TSalesOrderStatus.Refunded:
                salesOrder.refundedAt = now;
                salesOrder.completedAt = now;
                break;
            case SalesOrder_1.TSalesOrderStatus.Completed:
                salesOrder.completedAt = now;
                break;
            case SalesOrder_1.TSalesOrderStatus.Delivering:
                salesOrder.pickedUpByDeliveryDriverAt = now;
                break;
            case SalesOrder_1.TSalesOrderStatus.Ready:
                salesOrder.assembledAt = now;
                break;
            case SalesOrder_1.TSalesOrderStatus.Preparing:
                salesOrder.startedAssemblyAt = now;
                break;
            case SalesOrder_1.TSalesOrderStatus.Pending:
                // pending is handled by inserting a sales order
                break;
            case SalesOrder_1.TSalesOrderStatus.NotSubmitted:
                // we dont set any timestamp field for this
                break;
            default:
                throw new Error("unknown sales order status");
        }
        return salesOrder;
    }
    /**
     * Sync up the latest state of sales orders with those who are watching.
     */
    syncOrders() {
        this._emitter.emit("sync_data", this.salesOrders);
    }
    /**
     * Retrieve details of a particular sales order
     */
    async retrieveSalesOrderById(salesOrderId) {
        return this.salesOrders.find(o => o.salesOrderId === salesOrderId);
    }
    /**
     * Really just a way for a retail customer to checkout their sales order
     */
    async createSalesOrder(salesOrder) {
        // attach total price and promotions with data from the temporary sales order
        const temporarySalesOrderId = salesOrder.salesOrderId;
        const temporarySalesOrder = this.temporarySalesOrders.find(o => o.salesOrderId === temporarySalesOrderId);
        salesOrder.promotions = temporarySalesOrder.promotions;
        salesOrder.totalPrice = temporarySalesOrder.totalPrice;
        // create a new sales order id
        salesOrder.salesOrderId = (0, guid_1.createGuid)();
        salesOrder.createdAt = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        this.salesOrders.push(salesOrder);
        this.syncOrders();
        return salesOrder;
    }
    /**
     * Update a sales order to the given status
     * This will also send updates to the retail customer and retail location
     */
    async updateSalesOrder(salesOrderId, status) {
        const toUpdate = this.salesOrders.find(o => o.salesOrderId === salesOrderId);
        if (!toUpdate) {
            throw new Error("Could not find sales order.");
        }
        const updatedSalesOrder = this._setSalesOrderStatusTimestamp(toUpdate, status);
        const _salesOrders = this.salesOrders.filter(o => o.salesOrderId !== salesOrderId);
        _salesOrders.push(updatedSalesOrder);
        this.salesOrders = _salesOrders;
        this.syncOrders();
    }
    /**
     * Refund a retail customer's sales order
     */
    async refundSalesOrder(salesOrder) {
        const toRefund = this.salesOrders.find(o => o.salesOrderId === salesOrder.salesOrderId);
        if (!toRefund) {
            throw new Error("Could not find sales order.");
        }
        salesOrder.salesOrderRefund.salesOrderRefundId = (0, guid_1.createGuid)();
        salesOrder.salesOrderRefund.createdAt = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const refunded = this._setSalesOrderStatusTimestamp(salesOrder, SalesOrder_1.TSalesOrderStatus.Refunded);
        const _salesOrders = this.salesOrders.filter(o => o.salesOrderId !== refunded.salesOrderId);
        _salesOrders.push(refunded);
        this.salesOrders = _salesOrders;
        this.syncOrders();
    }
    /**
     * Retrieve a temporary sales order.
     * This is used for populating the cart.
     */
    async retrieveTemporarySalesOrder(salesOrderId) {
        return mockSalesOrders_1.mockSalesOrders[0];
    }
    /**
     * Update a temporary sales order.
     * This is used for updating the cart with the updated temporary
     * sales order that has any applicable promotion code attached
     * and total price re-calculated from the backend.
     *
     * If the temporary sales order does not have a salesOrderId
     * (this is the case when the customer adds the first item to
     * an empty cart) or a temporary sales order with the provided
     * salesOrderId cannot be found for some reason, the backend
     * will create a new temporary sales order and return it .
     */
    async setTemporarySalesOrder(salesOrder) {
        // reset promotion codes
        salesOrder.promotions = [];
        // Calculate total price and check if any promotion code is
        // applicable to the provided temporary sales order.
        const rawTotalPrice = salesOrder.salesOrderItems.reduce((total, current) => (total * 100 + current.price * 100) / 100, 0);
        // Mock promotion rule - 10% off when you spend $25.
        if (rawTotalPrice >= 25) {
            const priceDiscounted = rawTotalPrice * 0.1;
            salesOrder.totalPrice = parseFloat((rawTotalPrice - priceDiscounted).toFixed(2));
            salesOrder.promotions = [
                {
                    description: "10% off when you spend $25",
                    priceDiscounted: parseFloat(priceDiscounted.toFixed(2)),
                    type: IPromotion_1.TPromotionType.Total
                }
            ];
        }
        else {
            salesOrder.totalPrice = parseFloat(rawTotalPrice.toFixed(2));
        }
        if (!salesOrder.salesOrderId) {
            // the provided temporary sales order does not have a sales order id,
            // create one (new temporary sales order)
            salesOrder.salesOrderId = (0, guid_1.createGuid)();
            this.temporarySalesOrders.push(salesOrder);
            return salesOrder;
        }
        // the provided temporary sales order has a sales order id,
        // check if it exists
        const i = this.temporarySalesOrders.findIndex(o => o.salesOrderId === salesOrder.salesOrderId);
        if (i === -1) {
            // the provided temporary sales order does not exist, create one
            salesOrder.salesOrderId = (0, guid_1.createGuid)();
            this.temporarySalesOrders.push(salesOrder);
        }
        else {
            // the provided temporary sales order exists, update it
            this.temporarySalesOrders[i] = salesOrder;
        }
        return salesOrder;
    }
    /**
     * Delete a temporary sales order.
     */
    async deleteTemporarySalesOrder(salesOrderId) {
        const i = this.temporarySalesOrders.findIndex(o => o.salesOrderId === salesOrderId);
        if (i !== -1) {
            this.temporarySalesOrders.splice(i, 1);
        }
    }
    /**
     * Retrieve sales orders for a given retail location to reprint
     */
    retrieveSyncedRetailLocationSalesOrders(retailLocationId, type, params) {
        return {
            mockData: this.salesOrders,
            emitter: this._emitter
        };
    }
    /**
     * Retrieve retail customer's by given type
     * Currently - ["Outstanding"]
     */
    retrieveSyncedRetailCustomerSalesOrders(retailCustomerId, type) {
        let salesOrders;
        switch (type) {
            case salesOrder_1.TCustomerSalesOrder.Outstanding:
                salesOrders = this.salesOrders.filter((salesOrder) => salesOrder.retailCustomer.retailCustomerId === retailCustomerId &&
                    salesOrder.outstanding);
                break;
            case salesOrder_1.TCustomerSalesOrder.Past:
                break;
            default:
                throw new Error(`unknown customer sales order type - ${type}`);
        }
        return {
            mockData: salesOrders,
            emitter: this._emitter
        };
    }
    retrieveRetailCustomerSalesOrders(retailCustomerId, type, params) {
        const salesOrders = this.salesOrders.filter((salesOrder) => salesOrder.retailCustomer.retailCustomerId === retailCustomerId &&
            salesOrder.completed);
        return new Promise(res => res(salesOrders));
    }
    /**
     * Update a sales order's print status such that it can be synced to the
     * manager for monitoring
     */
    async updateSalesOrderPrintStatus(salesOrderId, printInfo) {
        const toUpdate = this.salesOrders.find(o => o.salesOrderId === salesOrderId);
        if (!toUpdate) {
            throw new Error("Could not find sales order.");
        }
        toUpdate.printInfo = printInfo;
        const _salesOrders = this.salesOrders.filter(o => o.salesOrderId !== salesOrderId);
        _salesOrders.push(toUpdate);
        this.salesOrders = _salesOrders;
        this.syncOrders();
    }
    async sendSalesOrderInvoice(salesOrderId) {
        return;
    }
    async cancelSalesOrder(salesOrderId) {
        this.salesOrders = this.salesOrders.map((order) => {
            if (order.salesOrderId === salesOrderId) {
                const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
                order.cancelledAt = timestampNow;
                order.completedAt = timestampNow;
            }
            return order;
        });
    }
};
MockRetailSalesOrdersModule = __decorate([
    (0, inversify_1.injectable)()
], MockRetailSalesOrdersModule);
exports.MockRetailSalesOrdersModule = MockRetailSalesOrdersModule;
//# sourceMappingURL=MockRetailSalesOrdersModule.js.map